/** Full-screen blockers pause automatic refreshes, never the action or its progress transport. */
const blockers = new Set<symbol>();
const pending = new Set<() => void>();
const listeners = new Set<() => void>();

export const isBackgroundRefreshPaused = () => blockers.size > 0;
export function subscribeBackgroundRefresh(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function pauseBackgroundRefresh(): () => void {
  const blocker = Symbol();
  blockers.add(blocker);
  if (blockers.size === 1) listeners.forEach(listener => listener());
  return () => {
    if (!blockers.delete(blocker) || blockers.size) return;
    // A replacement overlay may mount in the same commit (or StrictMode replay).
    queueMicrotask(() => {
      if (!isBackgroundRefreshPaused()) listeners.forEach(listener => listener());
      for (const flush of [...pending]) {
        if (isBackgroundRefreshPaused()) break;
        if (!pending.delete(flush)) continue;
        try { flush(); } catch (error) {
          // Keep independent owners live; surface the failed callback through the normal error path.
          queueMicrotask(() => { throw error; });
        }
      }
    });
  };
}

/** One deferred refresh per owner; dispose on unmount, disabling or scope change. */
export function createBackgroundRefresh(refresh: () => void) {
  let disposed = false;
  const flush = () => { if (!disposed) refresh(); };
  return {
    request() {
      if (disposed) return;
      if (isBackgroundRefreshPaused()) pending.add(flush);
      else { pending.delete(flush); flush(); }
    },
    dispose() { disposed = true; pending.delete(flush); }
  };
}
